import React from 'react';
import { View, TouchableOpacity, StyleSheet, Platform } from 'react-native';

import BaseText from './BaseText';
import { CommonProperties, Color } from '../styles';
import { scale } from '../utils/scale.utils';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../i18n';

const HEIGHT = scale(23);
const COLOR = Color.primary;
const TEXT_COLOR = 'black';
const DISABLE_TEXT_COLOR = 'grey';

const Crumb = ({
  isCrumbActive,
  text,
  firstCrumbStyle,
  lastCrumbStyle,
  crumbStyle,
  activeCrumbStyle,
  crumbTextStyle,
  activeCrumbTextStyle,
  height,
  triangleHeadStyle,
  triangleTailStyle,
}) => {
  const { t } = useTranslation(i18nNS.checkout);
  const { tKey } = useComponentTKey('CheckoutStepper');
  return (
    <TouchableOpacity
      style={[
        styles.crumbStyle,
        crumbStyle,
        isCrumbActive ? [styles.activeCrumbStyle, activeCrumbStyle] : {},
        firstCrumbStyle,
        lastCrumbStyle,
        { height },
      ]}
      activeOpacity={1}
    >
      <View style={styles.crumbContainer}>
        {isCrumbActive && !firstCrumbStyle ? (
          <View
            style={
              Platform.OS === 'android'
                ? [styles.leftTriangleContainer, styles.androidTriangleBack, { left: -height / 2 }]
                : [
                    styles.leftTriangleContainer,
                    styles.triangleTail,
                    triangleTailStyle,
                    { left: -height / 2 },
                  ]
            }
          />
        ) : null}
        <BaseText
          style={
            Platform.OS === 'android'
              ? [
                  styles.crumbTextStyle,
                  crumbTextStyle,
                  isCrumbActive
                    ? [styles.activeCrumbTextStyle, activeCrumbTextStyle, { marginLeft: 10 }]
                    : {},
                ]
              : [
                  styles.crumbTextStyle,
                  crumbTextStyle,
                  isCrumbActive ? [styles.activeCrumbTextStyle, activeCrumbTextStyle] : {},
                ]
          }
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {'  ' + t(tKey(text)) + ' '}
        </BaseText>
        {isCrumbActive && !lastCrumbStyle ? (
          <View
            style={
              Platform.OS === 'android'
                ? [
                    styles.rightTriangleContainer,
                    styles.androidTriangleFront,
                    { right: -height / 2 },
                  ]
                : [
                    styles.rightTriangleContainer,
                    styles.triangleHead,
                    triangleHeadStyle,
                    { right: -height / 2 },
                  ]
            }
          />
        ) : null}
      </View>
    </TouchableOpacity>
  );
};

const Breadcrumb = ({
  flowDepth,
  entities,
  borderRadius = scale(5),
  crumbsContainerStyle,
  crumbStyle,
  activeCrumbStyle,
  crumbTextStyle,
  activeCrumbTextStyle,
  height = scale(30),
}) => {
  const firstCrumbStyle = [
    { borderTopLeftRadius: borderRadius - 1, borderBottomLeftRadius: borderRadius - 1 },
  ];
  const lastCrumbStyle = [
    { borderTopRightRadius: borderRadius - 1, borderBottomRightRadius: borderRadius - 1 },
  ];
  const triangleTailStyle = height
    ? {
        borderTopWidth: height / 2.0,
        borderBottomWidth: height / 2.0,
        borderLeftWidth: height / 2.0,
      }
    : {};
  const triangleHeadStyle = height
    ? {
        borderTopWidth: height / 2.0,
        borderBottomWidth: height / 2.0,
        borderLeftWidth: height / 2.0,
      }
    : {};

  if (activeCrumbStyle && activeCrumbStyle.backgroundColor) {
    triangleTailStyle.borderTopColor = activeCrumbStyle.backgroundColor;
    triangleTailStyle.borderBottomColor = activeCrumbStyle.backgroundColor;
    triangleHeadStyle.borderColor = activeCrumbStyle.backgroundColor;
  }

  return (
    <View
      style={[
        styles.crumbsContainerStyle,
        borderRadius ? { borderRadius } : {},
        crumbsContainerStyle,
      ]}
      removeClippedSubviews={false}
    >
      {entities.map((item, index) => {
        return (
          <Crumb
            key={index}
            index={index}
            isCrumbActive={flowDepth === index}
            text={item}
            firstCrumbStyle={index === 0 ? firstCrumbStyle : null}
            lastCrumbStyle={index === entities.length - 1 ? lastCrumbStyle : null}
            crumbStyle={crumbStyle}
            activeCrumbStyle={activeCrumbStyle}
            crumbTextStyle={[
              crumbTextStyle,
              index < flowDepth ? { color: TEXT_COLOR } : { color: DISABLE_TEXT_COLOR },
            ]}
            activeCrumbTextStyle={activeCrumbTextStyle}
            height={height ? height : HEIGHT}
            triangleTailStyle={triangleTailStyle}
            triangleHeadStyle={triangleHeadStyle}
          />
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  crumbsContainerStyle: {
    backgroundColor: Color.transparent,
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: '#d6d6d6',
    justifyContent: 'space-between',
  },
  crumbStyle: {
    flexDirection: 'row',
    justifyContent: 'center',
    backgroundColor: Color.transparent,
    height: HEIGHT,
  },
  activeCrumbStyle: {
    backgroundColor: COLOR,
    borderColor: COLOR,
    paddingHorizontal: 1,
  },
  crumbTextStyle: {
    color: COLOR,
    ...CommonProperties.small,
  },
  activeCrumbTextStyle: {
    color: Color.accent,
    fontWeight: '600',
  },
  crumbContainer: {
    flexDirection: 'row',
    alignSelf: 'center',
    alignItems: 'center',
    ...CommonProperties.px3,
  },
  rightTriangleContainer: {
    position: 'absolute',
    right: 0,
    zIndex: 1,
  },
  leftTriangleContainer: {
    position: 'absolute',
    left: 0,
    zIndex: 1,
  },
  triangleHead: {
    borderTopWidth: HEIGHT / 2.0,
    borderRightWidth: 0,
    borderBottomWidth: HEIGHT / 2.0,
    borderLeftWidth: HEIGHT / 2.0,
    borderColor: COLOR,
    borderTopColor: Color.transparent,
    borderBottomColor: Color.transparent,
  },
  triangleTail: {
    borderTopWidth: HEIGHT / 2.0,
    borderRightWidth: 0,
    borderBottomWidth: HEIGHT / 2.0,
    borderLeftWidth: HEIGHT / 2.0,
    borderColor: Color.transparent,
    borderTopColor: COLOR,
    borderBottomColor: COLOR,
  },
  androidTriangleFront: {
    width: 0,
    height: 0,
    backgroundColor: Color.transparent,
    borderStyle: 'solid',
    borderLeftWidth: 16.7,
    borderRightWidth: 16.7,
    borderBottomWidth: 16.7,
    borderLeftColor: Color.transparent,
    borderRightColor: Color.transparent,
    borderBottomColor: Color.primary,
    transform: [{ rotate: '90deg' }, { translateY: -9 }],
  },
  androidTriangleBack: {
    width: 0,
    height: 0,
    backgroundColor: Color.transparent,
    borderStyle: 'solid',
    borderLeftWidth: 16.7,
    borderRightWidth: 16.7,
    borderBottomWidth: 14,
    borderLeftColor: Color.transparent,
    borderRightColor: Color.transparent,
    borderBottomColor: Color.background,
    transform: [{ rotate: '90deg' }, { translateY: -4 }],
  },
});

export default Breadcrumb;
